import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { usePlayoutState, useWsConnected } from '../../store/index.js'
import { logoProfilesApi, playoutApi } from '../../lib/api.js'
import type { LogoProfile, TimingTraceEntry } from '../../types/index.js'
import { useServerClock } from '../../hooks/useServerClock.js'
import './PlayoutDiagnostic.css'

function formatMs(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : ''
  return `${sign}${Math.round(value)} ms`
}

function latest(trace: TimingTraceEntry[], event: string): TimingTraceEntry | null {
  for (let i = trace.length - 1; i >= 0; i--) {
    if (trace[i].event === event) return trace[i]
  }
  return null
}

/**
 * Diagnostic playout : dérive, AMCP, moteur, préchargement et dernières traces.
 *
 * Vit dans Réglages et non dans le rail antenne : ce sont des données
 * d'exploitation qu'on consulte quand quelque chose ne va pas, pas des repères
 * qu'on surveille en continu pendant la conduite d'antenne. Le sondage des
 * traces (2 s) ne tourne donc que lorsque l'écran est ouvert.
 */
export const PlayoutDiagnostic = memo(function PlayoutDiagnostic({ channelId }: { channelId: string | null }) {
  const playout = usePlayoutState()
  const wsConnected = useWsConnected()
  const clock = useServerClock()
  const [trace, setTrace] = useState<TimingTraceEntry[]>([])
  const [logoProfiles, setLogoProfiles] = useState<LogoProfile[]>([])

  useEffect(() => {
    if (!channelId) { setTrace([]); return }
    let cancelled = false
    let failureCount = 0
    let timer: ReturnType<typeof setTimeout> | null = null

    const load = async () => {
      if (!wsConnected) {
        timer = setTimeout(load, 10_000)
        return
      }
      try {
        const res = await playoutApi.getTimingTrace(channelId, 200)
        failureCount = 0
        if (!cancelled) setTrace(res.entries)
      } catch {
        failureCount += 1
        if (!cancelled && failureCount <= 1) setTrace([])
      } finally {
        if (!cancelled) {
          const delay = failureCount > 0 ? Math.min(30_000, 2_000 * 2 ** Math.min(failureCount, 4)) : 2_000
          timer = setTimeout(load, delay)
        }
      }
    }
    void load()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [channelId, wsConnected])

  useEffect(() => {
    if (!channelId) { setLogoProfiles([]); return }
    let cancelled = false
    logoProfilesApi.list(channelId)
      .then(profiles => { if (!cancelled) setLogoProfiles(profiles) })
      .catch(() => { if (!cancelled) setLogoProfiles([]) })
    return () => { cancelled = true }
  }, [channelId])

  const lastPlay = latest(trace, 'PLAY_CONFIRMED')
  const lastEnd  = latest(trace, 'END_CONFIRMED')
  const lastAck  = [...trace].reverse().find(entry => entry.event === 'AMCP_ACK' && entry.commandType !== 'INFO') ?? null
  const lastLogo = [...trace].reverse().find(entry => String(entry.event).startsWith('LOGO_')) ?? null
  const lastRecovery = [...trace].reverse().find(entry => String(entry.event).startsWith('RECOVERY_')) ?? null
  const lastStale = latest(trace, 'STALE_EVENT_IGNORED')

  const drift = playout?.playoutDriftMs ?? lastPlay?.playoutDriftMs ?? null
  const bridgeOk = playout?.bridgeConnected ?? false
  const casparStatusMediaPath = typeof playout?.casparStatus?.casparMediaPath === 'string'
    ? playout.casparStatus.casparMediaPath
    : null
  const casparMediaPath = playout?.casparMediaPath ?? casparStatusMediaPath

  const logoNameById = useMemo(() => new Map(logoProfiles.map(profile => [profile.id, profile.name])), [logoProfiles])
  const logoLabel = useCallback((value: unknown) => {
    if (typeof value !== 'string' || !value) return 'aucun'
    return logoNameById.get(value) ?? value
  }, [logoNameById])

  const recentTrace = useMemo(() => [...trace].reverse().slice(0, 12), [trace])

  return (
    <div className="tp-diagnostic">
      <div className="tp-diagnostic__cards">
        {(playout?.targets ?? []).map(target => (
          <DiagnosticCard
            key={target.id}
            label={`${target.name} · ${target.active ? 'ANTENNE' : target.role}`}
            value={target.healthy ? 'SAIN' : target.connected ? 'DÉGRADÉ' : 'DÉCONNECTÉ'}
            tone={target.healthy ? 'ok' : target.connected ? 'warning' : 'danger'}
            detail={`frame ${target.frame} · AMCP ${target.probe.latencyMs} ms · médias ${target.preflight ? target.preflight.ok ? 'OK' : `${target.preflight.missing.length} absents` : '—'}`}
          />
        ))}
        {playout?.router && (
          <DiagnosticCard
            label="VideoHub"
            value={!playout.router.enabled ? 'DÉSACTIVÉ' : playout.router.connected ? 'CONNECTÉ' : 'EN ATTENTE'}
            tone={!playout.router.enabled ? 'neutral' : playout.router.connected ? 'ok' : 'warning'}
            detail={`${playout.router.host ?? '—'} · sortie ${playout.router.outputIndex ?? '—'}`}
          />
        )}
        <DiagnosticCard
          label="Moteur"
          value={bridgeOk ? 'CONNECTÉ' : 'DÉCONNECTÉ'}
          tone={bridgeOk ? 'ok' : 'danger'}
          detail={`frame ${playout?.casparFrame ?? playout?.frameCount ?? 0} · ${playout?.casparFps ?? '—'} fps`}
        />
        <DiagnosticCard
          label="AMCP"
          value={formatMs(lastAck?.responseMs)}
          tone={typeof lastAck?.responseMs === 'number' && lastAck.responseMs > 250 ? 'warning' : 'ok'}
          detail={lastAck?.commandType ? `dernier ${lastAck.commandType}` : 'aucun accusé récent'}
        />
        <DiagnosticCard
          label="Dérive courante"
          value={formatMs(drift)}
          tone={Math.abs(Number(drift ?? 0)) > 200 ? 'warning' : 'ok'}
          detail={`PLAY ${formatMs(playout?.lastPlayDeltaMs ?? lastPlay?.scheduledStartDeltaMs)} · END ${formatMs(playout?.lastEndDeltaMs ?? lastEnd?.scheduledEndDeltaMs)}`}
        />
        <DiagnosticCard
          label="Préchargement"
          value={playout?.runtimePhase ?? '—'}
          tone="neutral"
          detail={`recovery ${playout?.recoveryStatus ?? 'IDLE'}`}
        />
      </div>

      <div className="tp-diagnostic__columns">
        <dl className="tp-diagnostic__details">
          <div><dt>Horloge backend</dt><dd>{clock.status === 'SYNCED' ? 'synchronisée' : clock.status.toLowerCase()} · offset {formatMs(clock.offsetMs)} · RTT {clock.rttMs == null ? '—' : `${clock.rttMs} ms`}</dd></div>
          <div><dt>Média CasparCG</dt><dd>{casparMediaPath ?? '—'} · {typeof playout?.casparTimeSec === 'number' ? `${playout.casparTimeSec.toFixed(2)} s` : '—'}</dd></div>
          <div><dt>Logo antenne</dt><dd>actif {logoLabel(playout?.activeBugType)} · attendu {logoLabel(playout?.expectedBugType)} · dernier {playout?.lastLogoEvent ?? lastLogo?.event ?? '—'}</dd></div>
          <div><dt>Recovery</dt><dd>{playout?.lastRecoveryEvent ?? lastRecovery?.event ?? '—'}</dd></div>
          <div><dt>Événement obsolète</dt><dd>{lastStale ? `${lastStale.ignoredEvent ?? 'event'} (${lastStale.reason ?? 'sans raison'})` : 'aucun'}</dd></div>
          <div><dt>File de commandes</dt><dd>{playout?.commandQueueLen ?? 0} en attente</dd></div>
        </dl>

        <section className="tp-diagnostic__trace" aria-label="Derniers déclenchements">
          <h4 className="tp-diagnostic__trace-title">Derniers déclenchements</h4>
          {recentTrace.length === 0 ? (
            <p className="tp-diagnostic__trace-empty">Aucune trace disponible</p>
          ) : (
            <ol className="tp-diagnostic__trace-list">
              {recentTrace.map((entry, index) => (
                <li key={`${entry.ts}-${index}`}>
                  <span className="tp-diagnostic__trace-time">{new Date(entry.ts).toLocaleTimeString('fr-FR')}</span>
                  <span className="tp-diagnostic__trace-event">{entry.event}</span>
                  {entry.title && <span className="tp-diagnostic__trace-detail">{entry.title}</span>}
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  )
})

const DiagnosticCard = memo(function DiagnosticCard({
  label, value, detail, tone,
}: {
  label:  string
  value:  string
  detail: string
  tone:   'ok' | 'warning' | 'danger' | 'neutral'
}) {
  return (
    <div className={`tp-diagnostic__card is-${tone}`}>
      <div className="tp-diagnostic__card-label">{label}</div>
      <div className="tp-diagnostic__card-value">{value}</div>
      <div className="tp-diagnostic__card-detail">{detail}</div>
    </div>
  )
})
